import { useState, useCallback } from 'react'
import { FunctionsService } from '@/services/functions.service'
import type { AggregatedReport } from '@/lib/types'

interface UseSalesReportResult {
  report: AggregatedReport | null
  loading: boolean
  error: string | null
  getSalesReport: (from: Date, to: Date, forceShopify?: boolean) => Promise<AggregatedReport | null>
  clearError: () => void
  clearReport: () => void
}

export function useSalesReport(): UseSalesReportResult {
  const [report, setReport] = useState<AggregatedReport | null>(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const getSalesReport = useCallback(async (from: Date, to: Date, forceShopify = false) => {
    setLoading(true)
    setError(null)

    try {
      const response = await FunctionsService.getSalesReport(from, to, forceShopify)

      if (response.error) {
        setError(response.error)
        setReport(null)
        return null
      }

      if (response.data) {
        setReport(response.data)
        return response.data
      }

      setError('No se recibieron datos del servidor')
      return null
    } catch (err: any) {
      const errorMessage = err.message || 'Error inesperado al obtener reporte'
      setError(errorMessage)
      setReport(null)
      return null
    } finally {
      setLoading(false)
    }
  }, [])

  const clearError = useCallback(() => {
    setError(null)
  }, [])

  const clearReport = useCallback(() => {
    setReport(null)
  }, [])

  return {
    report,
    loading,
    error,
    getSalesReport,
    clearError,
    clearReport,
  }
}
